/**
 * PostHog Event Tracking Utilities
 * Following PostHog's official best practices for event naming and properties
 */

import { PostHog } from 'posthog-js';
import posthog from 'posthog-js';

// Debug flag for console logging of analytics
const ANALYTICS_DEBUG: boolean = (() => {
  try {
    const v = (import.meta as any)?.env?.VITE_ENABLE_ANALYTICS_DEBUG;
    if (typeof v === 'string') {
      const s = v.toLowerCase();
      if (s === '1' || s === 'true' || s === 'yes') return true;
    }
  } catch {}
  try {
    const w = (typeof window !== 'undefined' && (window as any)?.ENV?.VITE_ENABLE_ANALYTICS_DEBUG);
    if (typeof w === 'string') {
      const s = w.toLowerCase();
      if (s === '1' || s === 'true' || s === 'yes') return true;
    } else if (w === true) {
      return true;
    }
  } catch {}
  return false;
})();

// Event naming follows PostHog's "object_action" pattern
// Properties follow "object_adjective" pattern
export const PostHogEvents = {
  // User Authentication Events
  USER_SIGNED_UP: 'user_signed_up',
  USER_SIGNUP_FAILED: 'user_signup_failed',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGIN_FAILED: 'user_login_failed',
  USER_LOGGED_OUT: 'user_logged_out',
  USER_PASSWORD_RESET: 'user_password_reset',
  USER_EMAIL_VERIFIED: 'user_email_verified',
  TOKEN_REFRESHED: 'token_refreshed',
  
  // Retention & Session Events
  USER_ACTIVATED: 'user_activated',
  USER_RETAINED: 'user_retained',
  USER_RETAINED_DAY_1: 'user_retained_day_1',
  USER_RETAINED_DAY_7: 'user_retained_day_7',
  USER_RETAINED_DAY_30: 'user_retained_day_30',
  USER_DAILY_RETURN: 'user_daily_return',
  SESSION_STARTED: 'session_started',
  SESSION_ENDED: 'session_ended',
  DAILY_ACTIVITY_STARTED: 'daily_activity_started',
  RETENTION_MILESTONE_ACHIEVED: 'retention_milestone_achieved',
  CHURN_RISK_ASSESSED: 'churn_risk_assessed',

  // Learning Activity Events
  CHAT_CONVERSATION_STARTED: 'chat_conversation_started',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_SESSION_ENDED: 'chat_session_ended',
  DOCUMENT_UPLOAD_STARTED: 'document_upload_started',
  DOCUMENT_UPLOADED: 'document_uploaded',
  DOCUMENT_UPLOAD_FAILED: 'document_upload_failed',
  DOCUMENT_VIEWED: 'document_viewed',
  COURSE_ACCESSED: 'course_accessed',
  SKILL_PROGRESSED: 'skill_progressed',

  // User Engagement Events
  TYPING_SESSION_STARTED: 'typing_session_started',
  TYPING_SESSION_ENDED: 'typing_session_ended',
  MESSAGE_READ_TIME: 'message_read_time',
  FEATURE_EXPLORED: 'feature_explored',

  // Payment and Subscription Events
  PAYMENT_PAGE_VIEWED: 'payment_page_viewed',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_FAILED: 'checkout_failed',
  SUBSCRIPTION_CREATED: 'subscription_created',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',

  // Navigation Events (enhanced pageviews)
  PAGE_FOCUSED: 'page_focused',
  PAGE_BLURRED: 'page_blurred',
  NAVIGATION_CLICKED: 'navigation_clicked'
} as const;

// Type for event properties following PostHog conventions
export interface BaseEventProperties {
  session_id?: string;
  user_journey_stage?: 'new' | 'onboarding' | 'active' | 'returning';
  feature_area?: string;
  page_category?: string;
  user_tier?: 'free' | 'plus' | 'pro';
}

// Retention-specific event properties
export interface RetentionEventProperties extends BaseEventProperties {
  user_id?: string;
  days_since_signup?: number;
  days_since_last_login?: number;
  login_count?: number;
  retention_cohort?: 'day_0' | 'day_1' | 'week_1' | 'month_1' | 'quarter_1' | 'long_term';
  is_new_user?: boolean;
  is_returning_user?: boolean;
  retention_milestone?: string;
  churn_risk_level?: 'low' | 'medium' | 'high' | 'critical';
}

// Session event properties
export interface SessionEventProperties extends BaseEventProperties {
  session_id: string;
  duration_ms?: number;
  duration_minutes?: number;
  page_views?: number;
  is_new_session?: boolean;
  is_new_day?: boolean;
  total_sessions?: number;
  days_active_total?: number;
}

// Specific event property interfaces
export interface ChatEventProperties extends BaseEventProperties {
  conversation_id?: string;
  message_count?: number;
  word_count?: number;
  message_type?: 'question' | 'statement' | 'request';
  response_time_ms?: number;
}

export interface DocumentEventProperties extends BaseEventProperties {
  document_id?: string;
  file_type?: string;
  file_size_mb?: number;
  processing_time_ms?: number;
  document_category?: string;
}

export interface PaymentEventProperties extends BaseEventProperties {
  plan_name?: string;
  plan_price?: number;
  billing_cycle?: 'monthly' | 'yearly';
  currency?: string;
  payment_method?: string;
}

export interface EngagementEventProperties extends BaseEventProperties {
  time_spent_ms?: number;
  interaction_count?: number;
  scroll_depth_percentage?: number;
  element_clicked?: string;
}

// Utility functions for common tracking patterns
export class PostHogTracker {
  private posthog: PostHog;
  private isDevelopment: boolean;
  
  constructor(posthogInstance: PostHog) {
    this.posthog = posthogInstance;
    // Check if we're in development mode for enhanced logging
    this.isDevelopment = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || 
       window.location.hostname === '127.0.0.1' ||
       window.location.hostname.includes('dev'));
  }

  // Enhanced event tracking with debugging
  private captureEvent(eventName: string, properties: any = {}) {
    // Try to use the instance passed to constructor first
    let posthogInstance = this.posthog;
    
    // Fallback to global posthog if instance is not available
    if (!posthogInstance || !posthogInstance.capture) {
      if (ANALYTICS_DEBUG) console.warn(`⚠️ PostHog instance not available for event: ${eventName}, trying global instance`);
      
      // Try to use the global posthog instance
      if (typeof window !== 'undefined' && posthog && posthog.capture) {
        posthogInstance = posthog;
        if (ANALYTICS_DEBUG) console.log('✅ Using global PostHog instance as fallback');
      } else {
        if (ANALYTICS_DEBUG) console.error('🚨 No PostHog instance available at all for event:', eventName);
        return;
      }
    }

    // Add timestamp and session info
    const enhancedProperties = {
      timestamp: new Date().toISOString(),
      session_id: posthogInstance.get_session_id?.() || 'unknown',
      user_agent: navigator.userAgent,
      page_url: window.location.href,
      ...properties
    };

    try {
      posthogInstance.capture(eventName, enhancedProperties);
      if (ANALYTICS_DEBUG) console.log(`📊 PostHog event sent: ${eventName}`, enhancedProperties);
    } catch (error) {
      if (ANALYTICS_DEBUG) console.error('❌ Failed to send PostHog event:', eventName, error);
    }
  }

  // Track user authentication events
  trackAuth(eventName: string, properties: BaseEventProperties = {}) {
    this.captureEvent(eventName, {
      event_category: 'authentication',
      ...properties
    });
  }

  // Track learning activities with enhanced context
  trackLearning(eventName: string, properties: ChatEventProperties | DocumentEventProperties = {}) {
    this.captureEvent(eventName, {
      event_category: 'learning',
      feature_area: 'education',
      ...properties
    });
  }

  // Track payment and subscription events
  trackPayment(eventName: string, properties: PaymentEventProperties = {}) {
    this.captureEvent(eventName, {
      event_category: 'revenue',
      feature_area: 'subscription',
      ...properties
    });
  }

  // Track engagement metrics
  trackEngagement(eventName: string, properties: EngagementEventProperties = {}) {
    this.captureEvent(eventName, {
      event_category: 'engagement',
      ...properties
    });
  }

  // Track typing behavior (specific to learning platform)
  trackTypingSession(started: boolean, properties: { duration_ms?: number; characters_typed?: number } = {}) {
    const eventName = started ? PostHogEvents.TYPING_SESSION_STARTED : PostHogEvents.TYPING_SESSION_ENDED;
    this.captureEvent(eventName, {
      event_category: 'user_behavior',
      feature_area: 'chat_interaction',
      typing_session_started: started,
      ...properties
    });
  }

  // Track message reading patterns
  trackMessageReading(messageId: string, readingTime: number, messageLength: number) {
    this.captureEvent(PostHogEvents.MESSAGE_READ_TIME, {
      message_id: messageId,
      reading_time_ms: readingTime,
      message_length: messageLength,
      reading_speed_wpm: Math.round((messageLength / 5) / (readingTime / 60000)), // Approximate words per minute
      event_category: 'user_behavior',
      feature_area: 'content_consumption'
    });
  }

  // Track feature exploration
  trackFeatureExploration(featureName: string, timeSpent: number) {
    this.captureEvent(PostHogEvents.FEATURE_EXPLORED, {
      feature_name: featureName,
      exploration_time_ms: timeSpent,
      event_category: 'feature_adoption',
      feature_area: featureName.toLowerCase().replace(/\s+/g, '_')
    });
  }

  // Helper to identify users with enhanced properties
  identifyUser(userId: string, userProperties: Record<string, any> = {}) {
    if (!this.posthog) {
      console.error('🚨 PostHog instance not available for user identification');
      return;
    }

    try {
      const enhancedUserProperties = {
        // PostHog recommends these standard properties
        signup_date: userProperties.createdAt || new Date().toISOString(),
        user_tier: userProperties.subscriptionTier || 'free',
        email_domain: userProperties.email ? userProperties.email.split('@')[1] : undefined,
        onboarding_completed: userProperties.profileExists || false,
        last_active_date: new Date().toISOString(),
        identified_at: new Date().toISOString(),
        ...userProperties
      };

      this.posthog.identify(userId, enhancedUserProperties);

      // User identified in PostHog
    } catch (error) {
      console.error('❌ Failed to identify user in PostHog:', userId, error);
    }
  }
}

// Hook for accessing PostHog tracking utilities
export const usePostHogTracking = (posthogFromHook: PostHog | undefined) => {
  // Always create a tracker - it will handle fallback internally
  // Pass undefined if hook doesn't provide instance, tracker will use global
  const tracker = new PostHogTracker(posthogFromHook as any);
  
  // Log for debugging
  if (ANALYTICS_DEBUG && !posthogFromHook) {
    console.log('🔍 usePostHogTracking: No PostHog from hook, tracker will use fallback');
  }
  
  return {
    trackAuth: tracker.trackAuth.bind(tracker),
    trackLearning: tracker.trackLearning.bind(tracker),
    trackPayment: tracker.trackPayment.bind(tracker),
    trackEngagement: tracker.trackEngagement.bind(tracker),
    trackTypingSession: tracker.trackTypingSession.bind(tracker),
    trackMessageReading: tracker.trackMessageReading.bind(tracker),
    trackFeatureExploration: tracker.trackFeatureExploration.bind(tracker),
    identifyUser: tracker.identifyUser.bind(tracker)
  };
};
